import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  CheckCircle, Building2, ArrowRight, Key, Mail,
  Sparkles, Copy, ExternalLink, LayoutDashboard,
} from 'lucide-react';
import toast from 'react-hot-toast';
import type { Lot } from '../contexts/LotContext';
import { SUPPORT_EMAIL, SITE_NAME } from '../lib/config';

export default function OnboardingComplete() {
  const { lotId } = useParams<{ lotId: string }>();
  const [lot, setLot] = useState<Lot | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const isStripeCompletion = window.location.pathname.includes('/stripe-complete/');

  useEffect(() => {
    const fetchLot = async () => {
      if (!lotId) return;
      try {
        const apiUrl = window.location.hostname === 'localhost'
          ? `/api/complete-onboarding/${lotId}`
          : `/.netlify/functions/complete-onboarding/${lotId}`;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) throw new Error('Failed to complete onboarding');

        const data = await response.json();
        setLot(data.lot);
        toast.success('Setup complete!');
      } catch (error) {
        console.error('Error completing onboarding:', error);
        toast.error('Failed to complete onboarding');
      } finally {
        setLoading(false);
      }
    };
    fetchLot();
  }, [lotId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 border-2 border-confetti-pink/30 border-t-confetti-pink rounded-full animate-spin" />
          <p className="text-sm text-ink-500 font-medium">Completing your setup…</p>
        </div>
      </div>
    );
  }

  if (!lot) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lift p-8 max-w-sm w-full text-center">
          <div className="h-12 w-12 bg-cream-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-6 w-6 text-ink-400" />
          </div>
          <h2 className="text-xl font-display font-bold text-ink-900 mb-2">Setup not found</h2>
          <p className="text-sm text-ink-500 mb-6">
            We couldn't find your lot information. Please try the setup process again.
          </p>
          <Link
            to="/signup/lot-owner"
            className="inline-flex items-center gap-2 bg-confetti-pink text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-confetti-pink/90 transition-colors text-sm"
          >
            Start over
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  const publicUrl = `${window.location.origin}/lots/${lot.slug}`;

  const copyUrl = () => {
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      toast.success('URL copied!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const nextSteps = isStripeCompletion
    ? [
        { icon: CheckCircle, title: 'Payments are active', body: 'Your lot can now accept payments directly to your bank account.' },
        { icon: LayoutDashboard, title: 'Review your settings', body: 'Double-check pricing and lot details in your admin dashboard.' },
        { icon: Building2, title: 'Monitor your dashboard', body: 'Track payments and analytics through Stripe and your admin panel.' },
      ]
    : [
        { icon: Mail, title: 'Check your email', body: "We've sent your login credentials. Check your inbox (and spam folder)." },
        { icon: Key, title: 'Log in to your dashboard', body: 'Access your admin dashboard to manage settings and view analytics.' },
        { icon: Building2, title: 'Share your lot URL', body: 'Send customers your public lot page to start accepting bookings.', action: { label: publicUrl, onClick: copyUrl } },
      ];

  return (
    <div className="min-h-screen bg-cream-100 flex flex-col items-center justify-center py-12 px-4">
      {/* Wordmark */}
      <div className="mb-8 flex items-center gap-2">
        <div className="h-9 w-9 bg-confetti-pink rounded-xl flex items-center justify-center shadow-confetti">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <span className="font-display text-xl font-bold text-ink-900">{SITE_NAME}</span>
      </div>

      <div className="w-full max-w-lg">
        {/* Hero success card */}
        <div className="bg-white rounded-2xl shadow-lift overflow-hidden mb-5">
          {/* Gradient header */}
          <div className="relative bg-gradient-to-br from-confetti-pink to-confetti-coral px-8 py-10 text-white overflow-hidden">
            {/* decorative circles */}
            <div className="absolute -top-8 -right-8 h-32 w-32 bg-white/10 rounded-full" />
            <div className="absolute -bottom-10 -left-10 h-40 w-40 bg-white/10 rounded-full" />

            <div className="relative">
              <div className="h-14 w-14 bg-white/20 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-3xl font-display font-bold leading-tight">
                {isStripeCompletion ? 'Payment setup complete!' : "You're live! 🎉"}
              </h1>
              <p className="text-white/80 mt-2 text-sm leading-relaxed">
                {isStripeCompletion
                  ? 'Your Stripe Connect account is set up and ready to receive payments.'
                  : `${lot.display_name} is now live and accepting payments.`}
              </p>
            </div>
          </div>

          {/* Lot details */}
          <div className="px-8 py-6 border-b border-ink-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-ink-400 mb-1">Your Lot</p>
                <p className="font-display font-bold text-ink-900 text-lg">{lot.display_name}</p>
              </div>
              <span className="text-xs font-semibold bg-confetti-lime-soft text-ink-700 px-3 py-1 rounded-full">
                Active
              </span>
            </div>

            {/* Public URL copy row */}
            <div className="mt-4 flex items-center gap-2 bg-cream-100 rounded-xl px-4 py-3 border border-ink-300">
              <span className="text-xs font-mono text-ink-500 flex-1 truncate">{publicUrl}</span>
              <button
                onClick={copyUrl}
                className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-confetti-pink hover:text-confetti-pink/80 transition-colors"
              >
                <Copy className="h-3.5 w-3.5" />
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-ink-400 hover:text-ink-700 transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

          {/* Next steps */}
          <div className="px-8 py-6">
            <p className="text-xs font-bold uppercase tracking-widest text-ink-400 mb-4">Next Steps</p>
            <div className="space-y-4">
              {nextSteps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="flex items-start gap-4">
                    <div className="flex items-center justify-center h-8 w-8 rounded-full bg-confetti-pink text-white text-xs font-bold shrink-0">
                      {i + 1}
                    </div>
                    <div className="pt-0.5">
                      <p className="text-sm font-semibold text-ink-900">{s.title}</p>
                      <p className="text-xs text-ink-500 mt-0.5 leading-relaxed">{s.body}</p>
                      {s.action && (
                        <button
                          onClick={s.action.onClick}
                          className="mt-1.5 text-xs font-mono text-confetti-pink hover:underline"
                        >
                          {s.action.label}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Quick access */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <Link
            to={isStripeCompletion ? `/admin/lots/${lot.slug}/settings` : '/admin'}
            className="flex items-center gap-3 bg-white rounded-xl p-4 border border-ink-300 hover:border-confetti-pink/50 hover:shadow-soft transition-all group"
          >
            <div className="h-10 w-10 bg-confetti-pink rounded-xl flex items-center justify-center shrink-0 shadow-confetti group-hover:scale-105 transition-transform">
              <LayoutDashboard className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900 leading-tight">
                {isStripeCompletion ? 'Lot Settings' : 'Admin Dashboard'}
              </p>
              <p className="text-xs text-ink-400">Manage your lot</p>
            </div>
            <ArrowRight className="h-4 w-4 text-ink-300 group-hover:text-confetti-pink group-hover:translate-x-0.5 transition-all ml-auto shrink-0" />
          </Link>

          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-white rounded-xl p-4 border border-ink-300 hover:border-confetti-blue/50 hover:shadow-soft transition-all group"
          >
            <div className="h-10 w-10 bg-confetti-blue rounded-xl flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900 leading-tight">Public Page</p>
              <p className="text-xs text-ink-400">What customers see</p>
            </div>
            <ExternalLink className="h-4 w-4 text-ink-300 group-hover:text-confetti-blue transition-colors ml-auto shrink-0" />
          </a>
        </div>

        {/* Help footer */}
        <p className="text-center text-xs text-ink-400">
          Need help?{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-confetti-pink hover:underline font-medium">
            {SUPPORT_EMAIL}
          </a>
          {' '}· 10% platform fee on all sessions
        </p>
      </div>
    </div>
  );
}
